import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {LocationCardScratchpadState} from '../../scratchpad/locationCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import locationSetsApi from 'common/src/modules/locationSets/locationSets.api';
import {LocationSet} from 'common/src/modules/locationSets/locationSets.types';
import describeIdFormat from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/describeIdFormat';
import {idHelper} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.config';
import {IdPrefix} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.enums';
import formatParseError from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/formatParseError';

const setLocationSetToolSchema = z.object({
  locationSetId: z.string().describe(describeIdFormat(IdPrefix.LocationSet)),
});

export default function setLocationSetTool() {
  async function execute(
    params: ToolExecuteParams<typeof setLocationSetToolSchema, LocationCardScratchpadState>,
  ) {
    const {locationSetId} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<LocationCardScratchpadState>(
      agentScratchpad,
      NodeType.STORE,
    );

    if (!locationSetId) {
      return toolError('No location set ID provided');
    }

    if (!validatedScratchpad.card.locationFeedId) {
      return toolError('No location feed ID set for this card. Please set a location feed first.');
    }

    const parseResult = idHelper.parseLocationSetId(locationSetId);

    if (parseResult.ok === false) {
      return toolError(formatParseError(parseResult.error));
    }

    const numericId = parseResult.numericId;
    const availableSets = (await locationSetsApi.getLocationSets(
      validatedScratchpad.card.locationFeedId,
    )) as LocationSet[];

    if (!availableSets.some((s) => s.id === numericId)) {
      return toolError(
        `Location set ${idHelper.toLocationSetId(numericId)} is not available. Available location sets: ${availableSets.map((s) => `${idHelper.toLocationSetId(s.id)}: ${s.name}`).join(', ')}`,
      );
    }

    validatedScratchpad.card.locationSetId = numericId;

    return toolResult(`Location set set to: ${idHelper.toLocationSetId(numericId)}`);
  }

  return tool({
    name: V2CardAgentToolName.SetLocationSet,
    displayName: 'Setting location set',
    description: 'Set the location set for the card.',
    execute,
    inputSchema: setLocationSetToolSchema,
  });
}
